import { ObjectTools } from '../../../tools/object-tools';
import { BannerEntry } from '../../../services/banner.service';
import { SolBannerPlacement } from '../../../models/core/sol-banner';
import { SolText } from '../../../models/core/sol-text';

export interface SolBannerDot {
  readonly index: number;
  readonly current: boolean;
}

export interface SolBannerDotsStyle {
  readonly height: string;
  readonly transform: string;
}

/**
 * What the host shows: one entry of the published list, and the navigation for moving through the rest.
 *
 * The list stays in `BannerService` — this only holds where the reader is in it. What happens to that
 * position when a new list arrives follows the banner being read, because the app re-reads on focus, on
 * scope change and at a schedule boundary, and another feature may publish into the same surface at any
 * time: while that banner is still in the list the reader stays on it, wherever it has moved to, so
 * neither a re-read that changed nothing nor someone else's banner arriving moves them. Once it is gone
 * they start again at the first — except when they closed it, where holding the position lands them on
 * the next banner rather than back at the beginning.
 *
 * Stepping also names the animation the message arrives on, in a pair per direction: a CSS animation only
 * restarts when its name changes, so stepping the same way twice has to alternate between two identical
 * keyframes rather than leave the finished one in place and the second message unanimated.
 *
 * Every entry gets a dot, so the dots always say how long the set is, and `DOT_WINDOW` is how many are in
 * view — a longer set slides its track under that window rather than lengthening the rail, so the strip is
 * the same height for six banners as for sixty. Dots report position and nothing else: the two halves of
 * the rail carry the stepping, so a dot is never a target and never in the tab order.
 *
 * The track is wound back as a share of its own length rather than a distance, because every dot occupies
 * one whole slot, and the window's height is built from `DOT_SLOT` here so the count has a single home
 * rather than a copy in the stylesheet for someone to keep in step. A set shorter than the window sizes
 * that height down to the dots it actually holds rather than reporting a window it cannot fill.
 */
export class SolBannerHostViewState {
  public static readonly DOT_WINDOW = 3;
  public static readonly DOT_SLOT = 'var(--sol-size-8)';

  private constructor(
    public readonly entries: readonly BannerEntry[],
    public readonly index: number,
    public readonly dotOffset: number,
    public readonly placement: SolBannerPlacement,
    public readonly hostClass: string,
    public readonly current: BannerEntry | undefined,
    public readonly paged: boolean,
    public readonly atFirst: boolean,
    public readonly atLast: boolean,
    public readonly dots: readonly SolBannerDot[],
    public readonly position: SolText,
    public readonly dotsStyle: SolBannerDotsStyle,
    public readonly stepClass: string,
    public readonly stepParity: boolean,
  ) {}

  public static of(): SolBannerHostViewState {
    return new SolBannerHostViewState(
      [],
      0,
      0,
      'inline',
      '',
      undefined,
      false,
      true,
      true,
      [],
      SolText.empty(),
      { height: '', transform: '' },
      '',
      false,
    ).recalculate();
  }

  public withEntries(entries: readonly BannerEntry[]): SolBannerHostViewState {
    const moved = this.positionOf(entries);
    if (moved >= 0) {
      return this.copy({ entries, index: moved }).recalculate();
    }
    if (this.isCurrentClosed(entries)) {
      return this.copy({ entries }).recalculate();
    }
    return this.copy({ entries, index: 0, dotOffset: 0, stepClass: '' }).recalculateHostClass().recalculate();
  }

  public withPlacement(placement: SolBannerPlacement): SolBannerHostViewState {
    return this.copy({ placement }).recalculateHostClass();
  }

  public withIndex(index: number): SolBannerHostViewState {
    const bounded = SolBannerHostViewState.bound(index, this.entries.length);
    if (bounded === this.index) {
      return this;
    }
    const stepParity = !this.stepParity;
    return this.copy({
      index: bounded,
      stepParity,
      stepClass: SolBannerHostViewState.toStepClass(bounded > this.index, stepParity),
    })
      .recalculateHostClass()
      .recalculate();
  }

  private positionOf(entries: readonly BannerEntry[]): number {
    const current = this.current;
    return current === undefined ? -1 : entries.findIndex(entry => entry.id === current.id);
  }

  private isCurrentClosed(entries: readonly BannerEntry[]): boolean {
    if (entries.length !== this.entries.length - 1) {
      return false;
    }
    const remaining = this.entries.filter((_, position) => position !== this.index);
    return SolBannerHostViewState.sameEntries(remaining, entries);
  }

  private recalculate(): SolBannerHostViewState {
    const count = this.entries.length;
    const index = SolBannerHostViewState.bound(this.index, count);
    const dotOffset = SolBannerHostViewState.toDotOffset(this.dotOffset, index, count);
    const paged = count > 1;
    const dots = paged ? this.entries.map((_, position) => SolBannerHostViewState.toDot(position, index)) : [];
    return this.copy({
      index,
      dotOffset,
      current: this.entries[index],
      paged,
      atFirst: index === 0,
      atLast: index >= count - 1,
      dots,
      position: count > 0 ? SolText.of('sol-banner.position', { index: index + 1, total: count }) : SolText.empty(),
      dotsStyle: SolBannerHostViewState.toDotsStyle(dotOffset, dots.length),
    });
  }

  private recalculateHostClass(): SolBannerHostViewState {
    return this.copy({ hostClass: SolBannerHostViewState.toHostClass(this.placement, this.stepClass) });
  }

  private static toHostClass(placement: SolBannerPlacement, stepClass: string): string {
    return [placement === 'overlay' ? 'sol-banner-host-overlay' : '', stepClass].filter(part => part !== '').join(' ');
  }

  private static toStepClass(forward: boolean, parity: boolean): string {
    const direction = forward ? 'down' : 'up';
    return `sol-banner-step-${direction}-${parity ? 'a' : 'b'}`;
  }

  private static toDotsStyle(offset: number, dotCount: number): SolBannerDotsStyle {
    const slots = Math.min(dotCount, SolBannerHostViewState.DOT_WINDOW);
    const height = `calc(${SolBannerHostViewState.DOT_SLOT} * ${slots})`;
    if (dotCount < 1) {
      return { height, transform: 'none' };
    }
    return { height, transform: `translateY(calc(-100% * ${offset} / ${dotCount}))` };
  }

  private static toDotOffset(previous: number, index: number, count: number): number {
    const furthest = Math.max(0, count - SolBannerHostViewState.DOT_WINDOW);
    const held = Math.min(Math.max(previous, index - (SolBannerHostViewState.DOT_WINDOW - 1)), index);
    return Math.min(Math.max(held, 0), furthest);
  }

  private static toDot(position: number, index: number): SolBannerDot {
    return { index: position, current: position === index };
  }

  private static sameEntries(left: readonly BannerEntry[], right: readonly BannerEntry[]): boolean {
    return left.length === right.length && left.every((entry, position) => entry.id === right[position].id);
  }

  private static bound(index: number, count: number): number {
    return Math.min(Math.max(index, 0), Math.max(0, count - 1));
  }

  private copy(props: Partial<SolBannerHostViewState>): SolBannerHostViewState {
    return ObjectTools.copy<SolBannerHostViewState>(this, props);
  }
}
